/**
 * Opções ECharts para o elemento chart-widget (preview + export).
 */

const DEFAULT_COLORS = ['#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de', '#3ba272', '#fc8452', '#9a60b4'];

function stripUnsafe(s) {
  return String(s ?? '').replace(/</g, '').replace(/script/gi, '').slice(0, 200);
}

function numArray(arr) {
  if (!Array.isArray(arr)) return [];
  return arr.map((v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  });
}

export function buildMadriChartOption(content) {
  const c = content || {};
  const type = String(c.chartType || 'column').replace(/[^a-z-]/g, '') || 'column';
  const titleText = stripUnsafe(c.title);
  const colors = Array.isArray(c.colors) && c.colors.length
    ? c.colors.map((x) => String(x).replace(/["'<>;{}\\]/g, '').slice(0, 24)).filter(Boolean)
    : DEFAULT_COLORS;

  const categories = (Array.isArray(c.categories) ? c.categories : []).map((x) => stripUnsafe(x).slice(0, 80)).filter(Boolean);
  const cats = categories.length ? categories : ['A', 'B', 'C', 'D'];

  let seriesList = Array.isArray(c.series) ? c.series : [];
  if (!seriesList.length) {
    seriesList = [{ name: 'Série 1', data: [120, 200, 150, 180] }];
  }
  seriesList = seriesList.map((s, i) => ({
    name: stripUnsafe(s?.name || `Série ${i + 1}`).slice(0, 80) || `Série ${i + 1}`,
    data: numArray(s?.data).length ? numArray(s.data) : [10, 20, 30, 40],
  }));

  const itemsRaw = Array.isArray(c.items) ? c.items : [];
  let pieData = itemsRaw
    .filter((x) => x && (x.value != null && x.value !== ''))
    .map((x) => ({
    name: stripUnsafe(x.label || x.name || '').slice(0, 80) || '—',
    value: Math.max(0, Number(x.value) || 0),
  }));
  if (!pieData.length) {
    pieData = [
      { name: 'A', value: 40 },
      { name: 'B', value: 32 },
      { name: 'C', value: 28 },
    ];
  }

  const baseTitle = titleText ? { text: titleText, left: 'center', textStyle: { fontSize: 16 } } : undefined;
  const showLegend = c.showLegend !== false;

  const alignSeriesLength = (list, len) =>
    list.map((s) => ({
      ...s,
      data: (() => {
        const d = [...s.data];
        while (d.length < len) d.push(0);
        return d.slice(0, len);
      })(),
    }));

  switch (type) {
    case 'pie': {
      return {
        color: colors,
        title: baseTitle,
        tooltip: { trigger: 'item' },
        legend: showLegend ? { bottom: 4, left: 'center' } : { show: false },
        series: [
          {
            type: 'pie',
            radius: ['36%', '64%'],
            center: ['50%', '48%'],
            data: pieData,
            emphasis: { itemStyle: { shadowBlur: 10, shadowOffsetX: 0, shadowColor: 'rgba(0,0,0,0.25)' } },
          },
        ],
      };
    }
    case 'funnel': {
      const sorted = [...pieData].sort((a, b) => b.value - a.value);
      const maxVal = sorted.reduce((m, d) => Math.max(m, d.value), 0) || 1;
      return {
        color: colors,
        title: baseTitle,
        tooltip: { trigger: 'item' },
        legend: showLegend ? { type: 'scroll', bottom: 4, left: 'center', itemGap: 8 } : { show: false },
        series: [
          {
            type: 'funnel',
            name: 'Funil',
            left: 'center',
            top: titleText ? '12%' : '6%',
            width: '72%',
            height: showLegend ? '62%' : '72%',
            min: 0,
            max: maxVal,
            minSize: '14%',
            maxSize: '100%',
            sort: 'descending',
            gap: 3,
            label: {
              show: true,
              position: 'inside',
              formatter: '{b}\n{c}',
              color: '#fff',
              fontSize: 12,
            },
            labelLine: { show: false },
            itemStyle: {
              borderColor: '#fff',
              borderWidth: 1,
            },
            emphasis: {
              label: { fontSize: 13 },
              itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,0.2)' },
            },
            data: sorted,
          },
        ],
      };
    }
    case 'gauge': {
      const max = Math.max(1, Number(c.gaugeMax) || 100);
      let val = Number(c.gaugeValue);
      if (!Number.isFinite(val)) val = 65;
      val = Math.max(0, Math.min(max, val));
      return {
        title: baseTitle,
        series: [
          {
            type: 'gauge',
            startAngle: 200,
            endAngle: -20,
            min: 0,
            max,
            splitNumber: 10,
            progress: { show: true, width: 14 },
            axisLine: { lineStyle: { width: 14, color: [[1, '#e2e8f0']] } },
            axisTick: { show: true, length: 6, lineStyle: { color: '#94a3b8' } },
            splitLine: { length: 10, lineStyle: { color: '#94a3b8' } },
            axisLabel: { color: '#64748b', fontSize: 11, distance: 18 },
            pointer: { show: true, length: '65%', width: 5 },
            anchor: { show: true, size: 12, itemStyle: { color: '#0f172a' } },
            title: { show: false },
            detail: {
              valueAnimation: true,
              formatter: '{value}',
              fontSize: 26,
              fontWeight: 700,
              color: '#0f172a',
            },
            data: [{ value: val }],
          },
        ],
      };
    }
    case 'line':
      return {
        color: colors,
        title: baseTitle,
        tooltip: { trigger: 'axis' },
        legend: showLegend ? { bottom: 0 } : { show: false },
        grid: { left: '3%', right: '4%', bottom: showLegend ? 48 : 24, top: titleText ? 56 : 32, containLabel: true },
        xAxis: { type: 'category', boundaryGap: false, data: cats },
        yAxis: { type: 'value' },
        series: alignSeriesLength(seriesList, cats.length).map((s) => ({
          name: s.name,
          type: 'line',
          smooth: true,
          data: s.data,
        })),
      };
    case 'area':
      return {
        color: colors,
        title: baseTitle,
        tooltip: { trigger: 'axis' },
        legend: showLegend ? { bottom: 0 } : { show: false },
        grid: { left: '3%', right: '4%', bottom: showLegend ? 48 : 24, top: titleText ? 56 : 32, containLabel: true },
        xAxis: { type: 'category', boundaryGap: false, data: cats },
        yAxis: { type: 'value' },
        series: alignSeriesLength(seriesList, cats.length).map((s) => ({
          name: s.name,
          type: 'line',
          smooth: true,
          areaStyle: {},
          data: s.data,
        })),
      };
    case 'bar':
      return {
        color: colors,
        title: baseTitle,
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        legend: showLegend ? { bottom: 0 } : { show: false },
        grid: { left: '3%', right: '4%', bottom: showLegend ? 48 : 24, top: titleText ? 56 : 32, containLabel: true },
        xAxis: { type: 'value' },
        yAxis: { type: 'category', data: cats },
        series: alignSeriesLength(seriesList, cats.length).map((s) => ({
          name: s.name,
          type: 'bar',
          data: s.data,
        })),
      };
    case 'tower':
      return {
        color: colors,
        title: baseTitle,
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        legend: showLegend ? { bottom: 0 } : { show: false },
        grid: { left: '3%', right: '4%', bottom: showLegend ? 48 : 24, top: titleText ? 56 : 32, containLabel: true },
        xAxis: { type: 'category', data: cats },
        yAxis: { type: 'value' },
        series: alignSeriesLength(seriesList, cats.length).map((s) => ({
          name: s.name,
          type: 'bar',
          stack: 'torre',
          emphasis: { focus: 'series' },
          data: s.data,
        })),
      };
    case 'column':
    default:
      return {
        color: colors,
        title: baseTitle,
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        legend: showLegend ? { bottom: 0 } : { show: false },
        grid: { left: '3%', right: '4%', bottom: showLegend ? 48 : 24, top: titleText ? 56 : 32, containLabel: true },
        xAxis: { type: 'category', data: cats },
        yAxis: { type: 'value' },
        series: alignSeriesLength(seriesList, cats.length).map((s) => ({
          name: s.name,
          type: 'bar',
          data: s.data,
        })),
      };
  }
}
